/**
 * useStyles — TanStack Query wrapper around the visual-style preset surface
 * (`GET /api/v1/styles`).
 *
 * Returns the built-in style presets the document generator can render a
 * ``document_create`` against (accent / ink / surface + light/dark). The
 * Ando default ships FIRST (BE contract), so the picker / gallery can render
 * it as the "(default)" row without extra client logic.
 *
 * Fallback semantics: a 404 / 501 (endpoint not deployed yet) degrades to an
 * empty list so the picker stays alive and the user falls back to the
 * implicit Ando default (no ``_style_id`` emitted at execute time). Any other
 * error bubbles up so the picker can surface an error state.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { ApiError } from "@ando/core/api";

import { getStyles, type StylePreset } from "./api";

export function stylesQueryKey(): readonly unknown[] {
  return ["document-styles"] as const;
}

async function fetchOrFallback(): Promise<ReadonlyArray<StylePreset>> {
  try {
    return await getStyles();
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return [];
    }
    throw err;
  }
}

/**
 * Live style-preset list, BE-backed with an empty fallback.
 *
 * Staleness: 5 minutes — presets are static built-ins; we balance freshness
 * against the cost of refetching every time the proposal card mounts.
 */
export function useStyles(opts?: {
  /** Gate the fetch — defaults true (existing callers stay unchanged). */
  readonly enabled?: boolean;
}): UseQueryResult<ReadonlyArray<StylePreset>> {
  return useQuery<ReadonlyArray<StylePreset>>({
    queryKey: stylesQueryKey(),
    queryFn: fetchOrFallback,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
    enabled: opts?.enabled ?? true,
  });
}
